// Catalog.ID Client — Login View
// ============================================================================
// Pre-auth standalone view (no shell). Handles:
// 1. Username + password authentication
// 2. Device unlock (PIN / no-PIN / supersecret for new devices)
// 3. Transition to shell on success

import { createElement, createProgressBar, createCopyButton } from '@catalog-shell';
import type { ProgressBar } from '@catalog-shell';
import { createFactorInputs } from '../components/factor-inputs';
import { confirmAndChangePin } from '../components/device-mode-confirm';
import { modeFromUnlockFactors } from './device-mode-change';
import { renderDeviceSetup, encryptLabelForDevice, applyLabelToKnownDevice } from '../components/device-setup';
import { createSupersecretTextarea } from '../components/supersecret-textarea';
import * as accountApi from '../api/account';
import * as deviceApi from '../api/device';
import * as claimsApi from '../api/claims';
import { computeAuthHash, generatePassword } from '../crypto/credentials';
import { decryptKeyBag } from '../crypto/keybag';
import {
    parseBundle, decryptKeyBagBundle, type KeyBagBundle, type BundleBlob,
} from '../crypto/bundle';
import { restoreBlobsFromBundle } from '../crypto/bundle-blobs';
import * as blobApi from '../api/blobs';
import { createCardCollector, decodeResetCardFromFile, SHEET_UPLOAD_ACCEPT } from '../components/card-scan';
import { resetCardChecksum, type ResetCardPayload } from '../crypto/reset-card';
import { resolveResetCid } from './reset-card-scan';
import { reconstructSupersecret } from '../crypto/recovery-kit';
import { session } from '../state/session';
import { keys } from '../state/keys';
import { signStepUp } from './step-up';
import { identity } from '../state/identity';
import { eventSocket } from '../state/event-socket';
import * as deviceStorage from '../state/device-storage';
import { readDeviceKeyManifest } from '../state/device-key-selection';
import { rememberSlotKey } from '../state/device-slots';
import * as aes from '../crypto/aes';
import { api, isOriginOnlyError, isSessionRejected } from '../api/client';
import { originIsElsewhere, resolveOrigin, type OriginLookup } from '../state/origins';
import {
    loadOnboardingStash, clearOnboardingStash, stashedAccount, type StashedAccount,
} from './onboarding-stash';
import { resetOutcomeHeadline, resetOutcomeDetail, type ResetOutcome } from './reset-outcome';
import {
    describeAuthenticatorError, isCompleteAuthenticatorCode, normalizeAuthenticatorCode,
    submitTotpLogin,
} from './authenticator';
import {
    classifyUnlockFailure, initialUnlockFactors, nextAttemptAfter, routesToSupersecret,
    unlockWithFactors, type UnlockAttempt, type UnlockFactor, type UnlockOutcome,
} from './device-unlock';
import { isTotpRequired, type LoginResponse } from '../api/types';
import { base32Decode } from '../crypto/totp';
import { appendAuthenticatorKeyPanel } from '../components/authenticator-key-panel';
import {
    loginOptionsAfterSessionLoss, unlockAccountLine,
    type LoginViewOptions, type SessionLostInfo,
} from './session-lost';

export type { LoginViewOptions } from './session-lost';

export interface LoginViewCallbacks {
    onSuccess: () => void;
    onRegister: () => void;
    /** Present when the host can resume an unfinished registration from the
     *  onboarding stash — shows the resume/discard banner. */
    onResumeOnboarding?: () => void;
    /**
     * The unlock screen gave up its session: the server rejected the stored
     * token, or the user asked to log in as somebody else. Local state is
     * already adjusted (see {@link SessionLostInfo}); the host shows the login
     * form. Optional — by default the login form is rendered in place with
     * {@link loginOptionsAfterSessionLoss}. Hosts override to add affordances
     * the plain form lacks (the resume-registration banner).
     */
    onSessionLost?: (info: SessionLostInfo) => void;
}

/**
 * Give `callbacks` a session-lost handler when the host supplied none: render
 * the login form in the same container. The original callbacks are passed on,
 * so a second loss re-enters here rather than recursing on the wrapper.
 */
function withSessionLostDefault(container: HTMLElement, callbacks: LoginViewCallbacks): LoginViewCallbacks {
    if (callbacks.onSessionLost) return callbacks;
    return {
        ...callbacks,
        onSessionLost: (info) => renderLoginView(container, callbacks, loginOptionsAfterSessionLoss(info)),
    };
}

/**
 * Leave the identity the unlock screen was working on. `rejected` forgets the
 * dead token (it can never work again); `switch` keeps it — the identity
 * picker may switch back — and only clears the active identity, so the app
 * boots into the login form next time. Device enrolment stays in both cases:
 * after a fresh password login the device unlocks with its PIN as before,
 * rather than demanding the supersecret over a session hiccup.
 */
function leaveSession(cid: string | null, reason: SessionLostInfo['reason'], callbacks: LoginViewCallbacks): void {
    eventSocket.disconnect();
    if (reason === 'rejected') {
        if (cid) api.forgetSession(cid);
        else api.setToken(null);
    } else {
        api.setToken(null);
        deviceStorage.clearActiveCid();
    }
    identity.clear();
    callbacks.onSessionLost?.({ cid, reason });
}

/**
 * Render only the device unlock flow (skip login form).
 * Used when session token survives page reload but decrypted keys are lost.
 */
export function renderDeviceUnlockView(container: HTMLElement, callbacks: LoginViewCallbacks): void {
    container.innerHTML = '';
    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card' });

    const title = createElement('h2', { className: 'cid-auth-title', textContent: 'Catalog.ID' });
    card.appendChild(title);

    const errorArea = createElement('div', { className: 'cid-auth-error' });
    card.appendChild(errorArea);

    wrapper.appendChild(card);
    container.appendChild(wrapper);

    showDeviceUnlock(card, errorArea, withSessionLostDefault(container, callbacks));
}

/** What discarding an unfinished registration costs once its account exists. */
export function discardWarning(account: StashedAccount): string {
    const cost = 'the server removes it after 7 days';
    switch (account.kind) {
        case 'personal':
            return `Your Catalog.ID ${account.cid} has already been created. Discarding this registration `
                + `leaves it unfinished: ${cost}, and the registration code that paid for it is not returned.`;
        case 'organization':
            return `The organization ${account.cid} has already been created. Discarding this registration `
                + `leaves it unfinished: ${cost}, and the registration code that paid for it is not returned.`;
        case 'delegate':
            return `Your delegate account ${account.cid} has already been activated. Discarding this `
                + `activation leaves it unfinished: ${cost}, and the invitation cannot be used again.`;
    }
}

export function renderLoginView(
    container: HTMLElement, callbacks: LoginViewCallbacks, options: LoginViewOptions = {},
): void {
    container.innerHTML = '';
    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card' });

    const title = createElement('h2', { className: 'cid-auth-title', textContent: 'Catalog.ID' });
    card.appendChild(title);

    const errorArea = createElement('div', { className: 'cid-auth-error' });
    card.appendChild(errorArea);

    // Unfinished registration (onboarding stash) — surface it so it cannot
    // linger unnoticed; the user resumes or explicitly discards.
    const stash = loadOnboardingStash();
    if (stash && callbacks.onResumeOnboarding) {
        const banner = createElement('div', { className: 'cid-auth-resume-banner' });
        banner.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: `You have an unfinished registration from ${new Date(stash.savedAt).toLocaleString()}.`,
        }));
        const row = createElement('div', { className: 'cid-auth-link-row' });
        const resumeLink = createElement('a', { className: 'cid-auth-link', textContent: 'Resume registration' });
        resumeLink.setAttribute('href', '#');
        resumeLink.addEventListener('click', (e) => { e.preventDefault(); callbacks.onResumeOnboarding!(); });
        row.appendChild(resumeLink);
        const discardLink = createElement('a', { className: 'cid-auth-link', textContent: 'Discard' });
        discardLink.setAttribute('href', '#');
        discardLink.addEventListener('click', (e) => {
            e.preventDefault();
            // Once the wizard has created the account, this stash is the only
            // way to finish it (auth/wizard-exit.ts) — ask before throwing that
            // away. Before then there is nothing to lose.
            const account = stashedAccount(stash);
            if (!account) {
                clearOnboardingStash();
                banner.remove();
                return;
            }
            row.style.display = 'none';
            const confirmBox = createElement('div');
            confirmBox.appendChild(createElement('p', {
                className: 'cid-pane-error cid-pane-error--visible',
                textContent: discardWarning(account),
            }));
            const buttons = createElement('div', { className: 'cid-auth-link-row' });
            const keepBtn = createElement('button', {
                className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Keep it',
            });
            keepBtn.addEventListener('click', () => {
                confirmBox.remove();
                row.style.display = '';
            });
            const discardBtn = createElement('button', {
                className: 'cs-btn cs-btn--no cs-btn--small', textContent: 'Discard anyway',
            });
            discardBtn.addEventListener('click', () => {
                clearOnboardingStash();
                banner.remove();
            });
            buttons.appendChild(keepBtn);
            buttons.appendChild(discardBtn);
            confirmBox.appendChild(buttons);
            banner.appendChild(confirmBox);
        });
        row.appendChild(discardLink);
        banner.appendChild(row);
        card.appendChild(banner);
    }

    const form = createElement('div', { className: 'cid-auth-form' });

    if (options.notice) {
        form.appendChild(createElement('p', { className: 'cid-pane-note cid-auth-notice', textContent: options.notice }));
    }

    const cidInput = createInput('Username', 'text', 'cid-login-cid');
    form.appendChild(cidInput.wrapper);
    // An identity we already know about — the one whose session just ended, or
    // the one the identity picker was sent to without a stored token — is
    // prefilled; the user only types the password.
    const prefillCid = options.prefillCid ?? deviceStorage.getActiveCid() ?? '';
    if (prefillCid) cidInput.input.value = prefillCid;

    const passInput = createInput('Password', 'password', 'cid-login-pass');
    form.appendChild(passInput.wrapper);

    const loginBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Log in',
    });
    loginBtn.addEventListener('click', async () => {
        clearError();
        const cid = cidInput.input.value.trim();
        const password = passInput.input.value;
        if (!cid || !password) { showError('Enter username and password'); return; }

        loginBtn.setAttribute('disabled', '');
        loginBtn.textContent = 'Logging in...';

        try {
            const authHash = await computeAuthHash(password, cid);
            const home = await lookupHomeNode(cid);
            if (home?.kind === 'unreachable') {
                loginBtn.removeAttribute('disabled');
                loginBtn.textContent = 'Log in';
                showError(homeNodeUnreachable(home.code));
                return;
            }
            // A host that forwards elsewhere (the browser OIDC consent route,
            // docs/cid-governance.md M7b) takes over before anything is sent.
            if (home?.kind === 'found' && originIsElsewhere(home.origin) && options.onHomeNodeElsewhere) {
                options.onHomeNodeElsewhere(cid, home.origin);
                return;
            }
            const resp = await accountApi.login({ cid, authHash });

            // The Authenticator Key login gate (§15.5): the password was right,
            // but the account wants a code before it gets a session.
            if (isTotpRequired(resp)) {
                showTotpCodeStage(container, card, errorArea, callbacks, {
                    cid, preAuthToken: resp.preAuthToken,
                });
                return;
            }

            startSessionFromLogin(resp);
            showDeviceUnlock(card, errorArea, withSessionLostDefault(container, callbacks));
        } catch (err) {
            loginBtn.removeAttribute('disabled');
            loginBtn.textContent = 'Log in';
            // Still sent elsewhere after the client followed the node's answer
            // once: say so in words, and do not offer an unlock this node cannot do.
            if (isOriginOnlyError(err)) {
                showError(HOME_NODE_MOVED);
                return;
            }
            showError(err instanceof Error ? err.message : 'Login failed');
            unlockLink.style.display = '';
        }
    });
    form.appendChild(loginBtn);

    const linksRow = createElement('div', { className: 'cid-auth-link-row' });
    const registerLink = createElement('a', { className: 'cid-auth-link', textContent: 'Create a new identity' });
    registerLink.setAttribute('href', '#');
    registerLink.addEventListener('click', (e) => { e.preventDefault(); callbacks.onRegister(); });
    linksRow.appendChild(registerLink);

    // This exact wording is printed on the Account Unlock Sheet ("Open <node>
    // and choose 'Unlock account / reset password'") — keep them in step.
    const forgotLink = createElement('a', { className: 'cid-auth-link', textContent: 'Unlock account / reset password' });
    forgotLink.setAttribute('href', '#');
    forgotLink.addEventListener('click', (e) => {
        e.preventDefault();
        showResetPassword(card, errorArea, callbacks);
    });
    linksRow.appendChild(forgotLink);

    const unlockLink = createElement('a', { className: 'cid-auth-link', textContent: 'Account locked? Request unlock' });
    unlockLink.setAttribute('href', '#');
    unlockLink.style.display = 'none';
    unlockLink.addEventListener('click', (e) => {
        e.preventDefault();
        showRequestUnlock(card, errorArea, callbacks);
    });
    linksRow.appendChild(unlockLink);
    form.appendChild(linksRow);

    card.appendChild(form);
    wrapper.appendChild(card);
    container.appendChild(wrapper);

    (prefillCid ? passInput.input : cidInput.input).focus();
    const handleEnter = (e: KeyboardEvent) => { if (e.key === 'Enter') loginBtn.click(); };
    cidInput.input.addEventListener('keydown', handleEnter);
    passInput.input.addEventListener('keydown', handleEnter);

    function showError(msg: string): void { errorArea.textContent = msg; errorArea.style.display = 'block'; }
    function clearError(): void { errorArea.textContent = ''; errorArea.style.display = 'none'; }
}

// ============================================================================
// Device Unlock
// ============================================================================

function showDeviceUnlock(card: HTMLElement, errorArea: HTMLElement, callbacks: LoginViewCallbacks): void {
    const title = card.querySelector('.cid-auth-title');
    card.innerHTML = '';
    if (title) card.appendChild(title);
    card.appendChild(errorArea);
    errorArea.style.display = 'none';

    const cid = deviceStorage.getActiveCid();
    renderUnlockAccountRow(card, cid, callbacks);
    if (!cid) {
        showSupersecretUnlock(card, errorArea, callbacks);
        return;
    }

    const deviceToken = deviceStorage.getDeviceToken(cid);
    const hasKeys = deviceStorage.hasStoredKeys(cid);

    if (deviceToken && hasKeys) {
        void showExistingDeviceUnlock(card, errorArea, deviceToken, cid, callbacks);
    } else {
        showSupersecretUnlock(card, errorArea, callbacks);
    }
}

/**
 * Every unlock variant says WHOSE keys it is asking for, and offers a way out:
 * the person at the keyboard may not be the holder of this identity's PIN or
 * supersecret at all, or may simply want another account.
 */
function renderUnlockAccountRow(card: HTMLElement, cid: string | null, callbacks: LoginViewCallbacks): void {
    const row = createElement('div', { className: 'cid-auth-account-row' });
    if (cid) {
        row.appendChild(createElement('span', { className: 'cid-auth-account', textContent: unlockAccountLine(cid) }));
    }
    const link = createElement('a', { className: 'cid-auth-link', textContent: 'Log in as a different account' });
    link.setAttribute('href', '#');
    link.addEventListener('click', (e) => {
        e.preventDefault();
        leaveSession(cid, 'switch', callbacks);
    });
    row.appendChild(link);
    card.appendChild(row);
}

/** What the subtitle says while the named factors are being asked for. */
function unlockPromptFor(factors: readonly UnlockFactor[]): string | null {
    const pin = factors.includes('pin');
    const code = factors.includes('totp');
    if (pin && code) return 'Enter your PIN, then the code your authenticator app is showing';
    if (pin) return 'Enter your PIN';
    if (code) return 'Enter the code your authenticator app is showing';
    return null;
}

/**
 * One unlock attempt AND the local decrypt that follows it, classified into a
 * single vocabulary (auth/device-unlock.ts). The decrypt belongs here because
 * its failure — a device copy that lags or will not open — is an unlock failure
 * from the member's side and routes to exactly the same place.
 */
async function runDeviceUnlock(cid: string, attempt: UnlockAttempt): Promise<UnlockOutcome> {
    const outcome = await unlockWithFactors(attempt);
    if (outcome.status !== 'unlocked') return outcome;
    try {
        await loadKeysFromDevice(cid, aes.fromBase64(outcome.deviceEncryptionKey));
    } catch (err) {
        return classifyUnlockFailure(err);
    }
    return outcome;
}

async function showExistingDeviceUnlock(
    card: HTMLElement, errorArea: HTMLElement, deviceToken: string, cid: string, callbacks: LoginViewCallbacks,
): Promise<void> {
    // First paint from this device's local hint, so the screen names the
    // factors before the node has said anything (§6.1). The answer below
    // overrides it — the hint can be stale in both directions.
    const subtitle = createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: unlockPromptFor(initialUnlockFactors(cid)) ?? 'Unlocking device...',
    });
    card.appendChild(subtitle);

    // The BARE unlock, always first: a session created through the login code
    // gate carries a single-use in-memory pass, and only a code-less call
    // spends it (§5.3). Sending a code here would waste the pass and make the
    // member wait up to thirty seconds for the next code.
    const outcome = await runDeviceUnlock(cid, { deviceToken });

    // "Log in as a different account" may have replaced this screen while the
    // unlock was in flight; the user has left this identity — do not carry them
    // into its shell.
    if (!card.isConnected) {
        if (outcome.status === 'unlocked') keys.clear();
        return;
    }

    if (outcome.status === 'unlocked') {
        callbacks.onSuccess();
        return;
    }
    // The stored session is dead (expired, revoked, ended elsewhere, or a stale
    // token resurrected from a storage snapshot). No unlock variant can succeed
    // without a session — every one of them calls the server with this same
    // token — so the only honest next step is the login form.
    if (outcome.status === 'session-rejected') {
        leaveSession(cid, 'rejected', callbacks);
        return;
    }
    if (outcome.status === 'factors-required') {
        subtitle.textContent = unlockPromptFor(outcome.factors) ?? 'Unlock this device';
        showFactorUnlock(card, errorArea, subtitle, deviceToken, cid, outcome.factors, callbacks);
        return;
    }
    // Locked, unknown, or a device copy that will not open: re-typing a factor
    // cannot help. Only a supersecret unlock can, and its device-setup step
    // rewrites the whole local copy. The reason goes in the error area, not the
    // subtitle — `showSupersecretUnlock` rewrites the subtitle on the next line.
    errorArea.textContent = outcome.message;
    errorArea.style.display = 'block';
    showSupersecretUnlock(card, errorArea, callbacks);
}

/**
 * The factor screen (§6.2): a PIN pad, a six-digit code field, or both — always
 * exactly what the node's `factors_required` answer named, never what this
 * client guessed. The inputs themselves live in `components/factor-inputs.ts`,
 * shared with the mode-change confirm step (§15); this function owns the round
 * trip and the fallback link, and nothing else.
 */
function showFactorUnlock(
    card: HTMLElement,
    errorArea: HTMLElement,
    subtitle: HTMLElement,
    deviceToken: string,
    cid: string,
    initialFactors: readonly UnlockFactor[],
    callbacks: LoginViewCallbacks,
): void {
    const inputs = createFactorInputs({
        factors: initialFactors,
        pinLength: deviceStorage.getPinLength(cid) ?? 4,
        pinLengthChooser: true,
        onSubmit: submit,
    });
    card.appendChild(inputs.element);

    const fallbackRow = createElement('div', { className: 'cid-auth-link-row' });
    const fallbackLink = createElement('a', { className: 'cid-auth-link', textContent: 'Use supersecret instead' });
    fallbackLink.setAttribute('href', '#');
    fallbackLink.addEventListener('click', (e) => {
        e.preventDefault();
        toSupersecret();
    });
    fallbackRow.appendChild(fallbackLink);
    card.appendChild(fallbackRow);

    function toSupersecret(): void {
        inputs.element.remove();
        fallbackRow.remove();
        showSupersecretUnlock(card, errorArea, callbacks);
    }

    function showError(message: string): void {
        errorArea.textContent = message;
        errorArea.style.display = 'block';
    }

    async function submit(values: { pin?: string; code?: string }): Promise<void> {
        errorArea.style.display = 'none';
        const attempt: UnlockAttempt = {
            deviceToken, factors: inputs.factors, pin: values.pin, code: values.code,
        };
        const outcome = await runDeviceUnlock(cid, attempt);
        if (!card.isConnected) {
            if (outcome.status === 'unlocked') keys.clear();
            return;
        }

        if (outcome.status === 'unlocked') {
            if (inputs.factors.includes('pin')) deviceStorage.setPinLength(cid, inputs.pinLength);
            callbacks.onSuccess();
            return;
        }
        if (outcome.status === 'session-rejected') {
            leaveSession(cid, 'rejected', callbacks);
            return;
        }

        // What survives the retry is a decision, not a convenience: the node
        // verifies the PIN first and outside the code limiter, so a code
        // refusal proves the PIN was right and a PIN refusal means the code was
        // never reached (§1 item 3).
        const kept = nextAttemptAfter(outcome, values);

        if (outcome.status === 'factors-required') {
            // The row changed under us — another browser moved this device's
            // mode, or a gate pass covered the code. Repaint from the answer.
            inputs.keep(kept);
            inputs.setFactors(outcome.factors);
            subtitle.textContent = unlockPromptFor(outcome.factors) ?? 'Unlock this device';
            return;
        }

        showError(outcome.message);
        if (routesToSupersecret(outcome)) {
            toSupersecret();
            return;
        }
        // Wrong PIN resets the pad; a wrong code clears only the code field.
        inputs.keep(kept);
    }
}

function showSupersecretUnlock(
    card: HTMLElement, errorArea: HTMLElement, callbacks: LoginViewCallbacks,
): void {
    const subtitle = card.querySelector('.cid-auth-subtitle');
    if (subtitle) subtitle.textContent = 'Enter your supersecret to unlock keys';

    const form = createElement('div', { className: 'cid-auth-form' });

    // id `cid-supersecret` is referenced by e2e tests — preserve it.
    const ss = createSupersecretTextarea({ id: 'cid-supersecret', autoFocus: true });
    form.appendChild(ss.element);

    let progress: ProgressBar | null = null;

    // Recovery Kit Addendum §15 step 2: the key bag ciphertext can come from
    // the server replica (default) or from a member-held encrypted key backup
    // file (bundle) when the replica is unavailable or lost.
    let pendingBundle: { bundle: KeyBagBundle; fileName: string } | null = null;

    const unlockBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Unlock',
    }) as HTMLButtonElement;
    unlockBtn.disabled = true;
    ss.onValidityChange(valid => { unlockBtn.disabled = !valid; });
    unlockBtn.addEventListener('click', async () => {
        if (!ss.isValid()) return;
        const supersecret = ss.getValue();

        errorArea.style.display = 'none';
        unlockBtn.setAttribute('disabled', '');
        unlockBtn.textContent = pendingBundle ? 'Decrypting backup...' : 'Deriving key...';

        if (!progress) {
            progress = createProgressBar({ indeterminate: true, label: 'Deriving encryption key...' });
            form.insertBefore(progress.element, unlockBtn);
        }
        progress.setIndeterminate(true);

        try {
            // Audit M12: don't surface the supersecret-derived key out of this
            // scope — `decryptKeyBag`/`decryptKeyBagBundle` discard it after
            // decrypting the bag. Save-mode flows re-prompt on demand.
            let bag: import('../crypto/keybag').KeyBag;
            let bundleBlobs: BundleBlob[] = [];
            if (pendingBundle) {
                const expectedCid = session.getCid() ?? deviceStorage.getActiveCid() ?? undefined;
                ({ bag, blobs: bundleBlobs } =
                    await decryptKeyBagBundle(pendingBundle.bundle, supersecret, expectedCid));
            } else {
                bag = await decryptKeyBag(await accountApi.getKeyBag(), supersecret);
            }
            if (!card.isConnected) return;
            keys.load(bag);

            // A bundle restore also brings back the account's blobs
            // (cid-encrypted-storage §4.2): they are still ciphertext under
            // keys derived from the `derive_root` this very bag carries, so
            // pushing them needs nothing but the session. Only the ones the
            // server LACKS are pushed, as creates — a row that is already
            // there is newer than the bundle by definition.
            //
            // Best-effort, always: the member is unlocked either way, and an
            // unlock that failed over a wallet list would be a worse outcome
            // than a wallet list that has to be restored again later.
            let restoredBlobs = 0;
            if (bundleBlobs.length > 0) {
                progress.setLabel('Restoring your saved documents...');
                try {
                    ({ restored: restoredBlobs } =
                        await restoreBlobsFromBundle(bundleBlobs, blobApi));
                } catch (err) {
                    console.warn('[login] could not restore account blobs from the backup:', err);
                }
                if (!card.isConnected) return;
            }

            progress.setIndeterminate(false);
            progress.setLabel(restoredBlobs > 0
                ? `Keys unlocked · ${restoredBlobs} saved document${restoredBlobs === 1 ? '' : 's'} restored`
                : 'Keys unlocked');
            progress.setVariant('success');
            progress.setValue(100);

            // Show device setup unless the user explicitly chose "no local storage" mode.
            // Covers: new device (first login), locked device (PIN brute-force),
            // and voluntary "Use supersecret instead" (user may want to reconfigure).
            if (deviceStorage.getActiveCid() && deviceStorage.isNoLocalStorageMode(deviceStorage.getActiveCid()!)) {
                callbacks.onSuccess();
            } else {
                showDeviceResetChoice(card, errorArea, bag, callbacks);
            }
        } catch (err) {
            if (!card.isConnected) return;
            if (isSessionRejected(err)) {
                leaveSession(deviceStorage.getActiveCid(), 'rejected', callbacks);
                return;
            }
            progress?.setIndeterminate(false);
            progress?.setVariant('error');
            progress?.setLabel('Failed');
            unlockBtn.removeAttribute('disabled');
            unlockBtn.textContent = pendingBundle ? 'Restore' : 'Unlock';
            errorArea.textContent = err instanceof Error ? err.message : 'Failed to decrypt key bag';
            errorArea.style.display = 'block';
        }
    });
    form.appendChild(unlockBtn);

    // ── Restore from encrypted key backup file (bundle) ────────────────
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'application/json,.json';
    fileInput.id = 'cid-bundle-file';
    fileInput.style.display = 'none';
    form.appendChild(fileInput);

    const bundleNote = createElement('p', { className: 'cid-pane-note' });
    bundleNote.style.display = 'none';
    form.appendChild(bundleNote);

    const restoreRow = createElement('div', { className: 'cid-auth-link-row' });
    const restoreLink = createElement('a', {
        className: 'cid-auth-link',
        textContent: 'Restore from encrypted key backup file',
    });
    restoreLink.setAttribute('href', '#');
    restoreLink.addEventListener('click', (e) => { e.preventDefault(); fileInput.click(); });
    restoreRow.appendChild(restoreLink);
    form.appendChild(restoreRow);

    // ── Recover with Recovery Sheets (§15 path B) ────────────────────────
    // A quorum of scanned sheets reconstructs the supersecret into the field
    // above; the unlock itself then proceeds through either ciphertext source
    // (server key bag, or a bundle file selected via the link above).
    const cardsSection = createElement('div');
    cardsSection.style.display = 'none';
    const cardsRow = createElement('div', { className: 'cid-auth-link-row' });
    const cardsLink = createElement('a', {
        className: 'cid-auth-link',
        textContent: 'Recover with Recovery Sheets',
    });
    cardsLink.setAttribute('href', '#');
    cardsLink.addEventListener('click', (e) => {
        e.preventDefault();
        cardsSection.style.display = cardsSection.style.display === 'none' ? '' : 'none';
    });
    cardsRow.appendChild(cardsLink);
    form.appendChild(cardsRow);

    const collector = createCardCollector();
    cardsSection.appendChild(collector.element);
    const reconstructBtn = createElement('button', {
        className: 'cs-btn cs-btn--default cid-auth-submit',
        textContent: 'Reconstruct supersecret from sheets',
    }) as HTMLButtonElement;
    reconstructBtn.disabled = true;
    collector.onChange(cards => {
        reconstructBtn.disabled = cards.length === 0 || cards.length < cards[0].thresholdK;
    });
    reconstructBtn.addEventListener('click', () => {
        errorArea.style.display = 'none';
        try {
            const supersecret = reconstructSupersecret(collector.getCards());
            ss.textarea.value = supersecret;
            ss.refresh();
            cardsSection.style.display = 'none';
            bundleNote.textContent = pendingBundle
                ? bundleNote.textContent
                : 'Supersecret reconstructed from your Recovery Sheets. '
                    + 'Press Unlock to fetch your keys from the server, or pick a backup file above.';
            bundleNote.style.display = '';
        } catch (err) {
            errorArea.textContent = err instanceof Error ? err.message : 'Reconstruction failed';
            errorArea.style.display = 'block';
        }
    });
    cardsSection.appendChild(reconstructBtn);
    form.appendChild(cardsSection);

    fileInput.addEventListener('change', async () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        errorArea.style.display = 'none';
        try {
            const bundle = parseBundle(await file.text());
            pendingBundle = { bundle, fileName: file.name };
            unlockBtn.textContent = 'Restore';
            bundleNote.textContent = `Restoring from ${file.name} (exported ${bundle.created_at}). `
                + 'Enter your supersecret and press Restore. ';
            const cancel = createElement('a', { className: 'cid-auth-link', textContent: 'Use server copy instead' });
            cancel.setAttribute('href', '#');
            cancel.addEventListener('click', (e) => {
                e.preventDefault();
                pendingBundle = null;
                fileInput.value = '';
                unlockBtn.textContent = 'Unlock';
                bundleNote.style.display = 'none';
            });
            bundleNote.appendChild(cancel);
            bundleNote.style.display = '';
        } catch (err) {
            pendingBundle = null;
            fileInput.value = '';
            errorArea.textContent = err instanceof Error ? err.message : 'Could not read backup file';
            errorArea.style.display = 'block';
        }
    });

    // Ctrl/Cmd+Enter submits — plain Enter inserts a newline (matches multi-line spec).
    ss.textarea.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); unlockBtn.click(); }
    });

    card.appendChild(form);
}

/** After supersecret unlock, show device setup so the user picks a new PIN/mode. */
function showDeviceResetChoice(
    card: HTMLElement,
    _errorArea: HTMLElement,
    bag: import('../crypto/keybag').KeyBag,
    callbacks: LoginViewCallbacks,
): void {
    const cid = deviceStorage.getActiveCid()!;

    // Capture before renderDeviceSetup writes a new token to localStorage
    const isNewDevice = !deviceStorage.getDeviceToken(cid);

    const title = card.querySelector('.cid-auth-title');
    card.innerHTML = '';
    if (title) card.appendChild(title);

    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Set up this device',
    }));

    // Set once a `reset` has been refused for "not locked": the NEXT call from
    // this screen goes to `changePin` instead. Remembered rather than retried
    // in place because an Authenticator mode has to come back through the code
    // field for a FRESH code — see below.
    let resetRefusedNotLocked = false;

    /**
     * The re-key that `changePin` performs here is a MODE CHANGE like any other
     * (§15 A): a supersecret proves who the member is, but the node cannot see
     * that it was entered — the step-up it does see proves key custody, which
     * every caller of this screen has. So a re-key that drops the PIN or the
     * Authenticator factor presents that factor, through the same confirm step
     * the Devices pane uses. Nothing is owed when the mode only gains factors,
     * which is the common path off this screen.
     *
     * The current mode comes from this browser's unlock-factor hint — the only
     * record this screen has, and only a hint: the node's answer overrides it.
     */
    const rekey = (rest: Omit<import('../api/types').ChangePinRequest,
        'currentPin' | 'challengeSignature'>): Promise<void> =>
        confirmAndChangePin({
            host: card,
            current: modeFromUnlockFactors(
                initialUnlockFactors(cid), deviceStorage.getPinLength(cid)),
            request: rest,
            signStepUp: () => signStepUp('device-manage'),
            changePin: deviceApi.changePin,
        });

    renderDeviceSetup(card, bag, cid, {
        onSetup: async (params) => {
            // `label` is the PLAINTEXT device name — encrypt it to the account's
            // own attr_encrypt key before any of it reaches the server.
            const { label: plainLabel, ...rest } = params;
            const labelFields = await encryptLabelForDevice(cid, plainLabel);
            const req = { ...rest, ...labelFields };
            if (resetRefusedNotLocked) {
                // Second pass: the device is unlocked, so re-key it in place.
                // Both the challenge and (for an Authenticator mode) the code
                // are the fresh ones this pass carries.
                await rekey(rest);
                await applyLabelToKnownDevice(cid, labelFields);
                callbacks.onSuccess();
                return;
            }
            if (isNewDevice) {
                // `setup` takes no step-up — it only creates a row holding the
                // key this browser just produced.
                deviceStorage.setDeviceId(cid, (await deviceApi.setupDevice(req)).deviceId);
            } else {
                // `reset` rewrites an existing row (clears its lockout, replaces
                // its key), so the server wants proof of main_signing_key
                // possession. The bag was just unlocked with the supersecret and
                // keys.load()ed, so signing is available. Signed OUTSIDE the try
                // so a signing failure is never mistaken for a server refusal.
                const challengeSignature = await signStepUp('device-manage');
                try {
                    deviceStorage.setDeviceId(cid,
                        (await deviceApi.resetDevice({ ...req, challengeSignature })).deviceId);
                } catch (err) {
                    const msg = err instanceof Error ? err.message : '';
                    if (msg.includes('not found') || msg.includes('Not Found')) {
                        // Stale device token — server record was deleted (remote revocation,
                        // lockdown, key rotation). Only the SERVER row is gone: `doSetup` has
                        // just written this device's fresh copy, manifest and factor hint
                        // under the new DEK, and it re-sets the token (reused for the new row)
                        // after this callback returns. Wiping device storage here — which this
                        // branch did until 2026-09-17 — left a device row beside an EMPTY local
                        // copy, so the next unlock fell back to the supersecret every time.
                        deviceStorage.clearDeviceId(cid);
                        deviceStorage.setDeviceId(cid, (await deviceApi.setupDevice(req)).deviceId);
                    } else if (msg.includes('not locked')) {
                        // The device is unlocked, so this is a voluntary supersecret
                        // re-key (e.g. healing stale device-key copies after a crypto
                        // migration), not locked-device recovery. `reset` is
                        // locked-only; re-key the existing device in place instead
                        // (changePin updates DEK + access mode + PIN on an unlocked
                        // device). It is step-up gated too, and the refused reset
                        // already spent the challenge above — mint a fresh one.
                        resetRefusedNotLocked = true;
                        if (params.totpRequired) {
                            // …and a fresh CODE. The lock check runs before the
                            // node verifies the code (§3), so this one was not
                            // spent — but the next request must not send it
                            // again in case it was. Throwing here sends the
                            // member back to the code field, whose next submit
                            // takes the `resetRefusedNotLocked` path above.
                            throw new Error('Type the next code your app shows and confirm again.');
                        }
                        await rekey(rest);
                        // changePin carries no label — rename separately if we
                        // already know which device row is ours.
                        await applyLabelToKnownDevice(cid, labelFields);
                    } else {
                        throw err;
                    }
                }
            }
            callbacks.onSuccess();
        },
        onSkip: async () => {
            // User chose "no local storage" — clean up old device
            const oldToken = deviceStorage.getDeviceToken(cid);
            if (oldToken) {
                try { await deviceApi.removeDevice({ deviceToken: oldToken }); } catch { /* */ }
            }
            deviceStorage.clearDeviceStorage(cid);
            // Re-set the flag after clear (clearDeviceStorage wipes it)
            deviceStorage.setNoLocalStorageMode(cid);
            callbacks.onSuccess();
        },
    });
}

// ============================================================================
// Request Unlock
// ============================================================================

function showRequestUnlock(
    card: HTMLElement, errorArea: HTMLElement, callbacks: LoginViewCallbacks,
): void {
    const title = card.querySelector('.cid-auth-title');
    card.innerHTML = '';
    if (title) card.appendChild(title);
    card.appendChild(errorArea);
    errorArea.style.display = 'none';

    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Request account unlock',
    }));

    card.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: 'If your account has been locked, enter your Catalog.ID below to request an unlock. An operator will verify your identity and approve the request.',
    }));

    const form = createElement('div', { className: 'cid-auth-form' });

    const cidInput = createInput('Catalog.ID', 'text', 'cid-unlock-cid');
    form.appendChild(cidInput.wrapper);

    const submitBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Request Unlock',
    });
    submitBtn.addEventListener('click', async () => {
        const cid = cidInput.input.value.trim();
        if (!cid) {
            errorArea.textContent = 'Enter your Catalog.ID';
            errorArea.style.display = 'block';
            return;
        }

        submitBtn.setAttribute('disabled', '');
        submitBtn.textContent = 'Submitting...';
        errorArea.style.display = 'none';

        try {
            await accountApi.requestUnlock({ cid });
            // Always show the same message regardless of whether the account exists/is locked
            // (server returns the same response in all cases to prevent enumeration)
            form.innerHTML = '';
            card.appendChild(createElement('p', {
                className: 'cid-auth-subtitle',
                textContent: 'Request received',
            }));
            card.appendChild(createElement('p', {
                className: 'cid-text-muted',
                textContent: 'If this account exists and is locked, an operator will review your request. You will be able to log in and rotate your keys once the request is approved.',
            }));
            const backLink = createElement('a', { className: 'cid-auth-link', textContent: 'Back to login' });
            backLink.setAttribute('href', '#');
            backLink.addEventListener('click', (e) => {
                e.preventDefault();
                renderLoginView(card.parentElement!, callbacks);
            });
            const linkRow = createElement('div', { className: 'cid-auth-link-row' });
            linkRow.appendChild(backLink);
            card.appendChild(linkRow);
        } catch (err) {
            submitBtn.removeAttribute('disabled');
            submitBtn.textContent = 'Request Unlock';
            errorArea.textContent = err instanceof Error ? err.message : 'Request failed';
            errorArea.style.display = 'block';
        }
    });
    form.appendChild(submitBtn);

    const backRow = createElement('div', { className: 'cid-auth-link-row' });
    const backLink = createElement('a', { className: 'cid-auth-link', textContent: 'Back to login' });
    backLink.setAttribute('href', '#');
    backLink.addEventListener('click', (e) => {
        e.preventDefault();
        renderLoginView(card.parentElement!, callbacks);
    });
    backRow.appendChild(backLink);
    form.appendChild(backRow);

    card.appendChild(form);
    cidInput.input.focus();
    cidInput.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submitBtn.click(); });
}

// ============================================================================
// Account Unlock Sheet — unlock / password reset
// (password-reset plan §5.4.4; registration-restructure §12)
// ============================================================================

/**
 * "Unlock account / reset password" flow: scan the Account Unlock Sheet → choose
 * a channel → confirm the code → choose whether to set a new password → finish.
 * Completion always happens HERE — this client holds the only copy of the
 * resetSecret; the email link may be opened on any device and only confirms.
 *
 * §12: the same ceremony unlocks a locked account and (optionally) sets a new
 * password. The client never learns the account status, so it always offers the
 * password choice and lets the server report what actually changed.
 *
 * The CID is load-bearing: it routes the request to the home node and supplies
 * the Argon2id salt for the new authHash. It is public knowledge (printed on
 * the sheet), not an auth factor — the sheet + channel are.
 *
 * Amendment 2026-09-03: the scan comes FIRST and supplies the CID. Sheets carry
 * it in the QR's CIDA framing, so nothing is typed — a mistyped id used to land
 * on the server's anti-enumeration decoy and leave the member waiting for a mail
 * that was never sent. Only a sheet printed before the amendment still needs the
 * id typed, and the field appears only then.
 */
function showResetPassword(
    card: HTMLElement, errorArea: HTMLElement, callbacks: LoginViewCallbacks,
): void {
    const title = card.querySelector('.cid-auth-title');
    card.innerHTML = '';
    if (title) card.appendChild(title);
    card.appendChild(errorArea);
    errorArea.style.display = 'none';

    const showError = (msg: string) => {
        errorArea.textContent = msg;
        errorArea.style.display = 'block';
    };
    const clearError = () => { errorArea.style.display = 'none'; };

    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Unlock account / reset password',
    }));
    card.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: 'Your Account Unlock Sheet unlocks a locked account and can set a new password. '
            + 'You need the sheet and access to the email or phone it was issued against — a code is '
            + 'sent there every time. Start by scanning the sheet: it names your account.',
    }));

    const form = createElement('div', { className: 'cid-auth-form' });

    // Sheet scan FIRST: photo, scan or PDF upload (same path as Recovery Sheet
    // scanning — the iframed dashboard often has no camera permission, any phone
    // photo works, and the sheet Settings issues is itself a one-page PDF).
    // A sheet printed with the CIDA framing names its own account.
    let scanned: ResetCardPayload | null = null;
    const scanRow = createElement('div', { className: 'cid-auth-field' });
    const scanLabel = createElement('label', {
        className: 'cid-text-muted',
        textContent: 'Scan your Account Unlock Sheet — a photo, a scan, or the PDF',
    });
    const scanInput = document.createElement('input');
    scanInput.type = 'file';
    // The sheet Settings hands out is a one-page PDF download, so that file is
    // very often the only copy at hand when an account needs unlocking.
    scanInput.accept = SHEET_UPLOAD_ACCEPT;
    const scanStatus = createElement('p', { className: 'cid-text-muted' });
    scanRow.appendChild(scanLabel);
    scanRow.appendChild(scanInput);
    scanRow.appendChild(scanStatus);
    form.appendChild(scanRow);

    // Only ever shown for a legacy sheet whose QR carries no account id. Kept in
    // the DOM (same id as before) so it can be revealed in place.
    const cidInput = createInput('Catalog.ID', 'text', 'cid-reset-cid');
    cidInput.wrapper.style.display = 'none';
    const cidHint = createElement('p', {
        className: 'cid-text-muted',
        textContent: 'This sheet does not name its account — type the Catalog.ID printed on it.',
    });
    cidHint.style.display = 'none';
    form.appendChild(cidInput.wrapper);
    form.appendChild(cidHint);

    scanInput.addEventListener('change', async () => {
        const file = scanInput.files?.[0];
        if (!file) return;
        clearError();
        scanStatus.textContent = 'Reading sheet...';
        try {
            const payload = await decodeResetCardFromFile(file);
            scanned = payload;
            const checksum = resetCardChecksum(payload.sealedToken);
            if (payload.cid) {
                // The sheet names the account — nothing left to type.
                cidInput.wrapper.style.display = 'none';
                cidHint.style.display = 'none';
                scanStatus.textContent = `Account Unlock Sheet for ${payload.cid} — checksum ${checksum}.`;
            } else {
                cidInput.wrapper.style.display = '';
                cidHint.style.display = '';
                scanStatus.textContent = `Sheet read — checksum ${checksum}.`;
                cidInput.input.focus();
            }
        } catch (err) {
            scanned = null;
            cidInput.wrapper.style.display = 'none';
            cidHint.style.display = 'none';
            scanStatus.textContent = '';
            showError(err instanceof Error ? err.message : 'Could not read the sheet');
        }
    });

    // Channel choice.
    const channelRow = createElement('div', { className: 'cid-auth-field' });
    channelRow.appendChild(createElement('label', {
        className: 'cid-text-muted',
        textContent: 'Send the confirmation code to',
    }));
    const channelSelect = document.createElement('select');
    channelSelect.className = 'cid-auth-input';
    for (const [value, label] of [['email', 'My email'], ['phone', 'My phone (SMS)']]) {
        const opt = document.createElement('option');
        opt.value = value;
        opt.textContent = label;
        channelSelect.appendChild(opt);
    }
    channelRow.appendChild(channelSelect);
    form.appendChild(channelRow);

    const submitBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Send confirmation code',
    });
    submitBtn.addEventListener('click', async () => {
        const decision = resolveResetCid(scanned, cidInput.input.value);
        if (decision.cid === undefined) { showError(decision.error!); return; }
        const cid = decision.cid;
        const sealedToken = scanned!.sealedToken;

        clearError();
        submitBtn.setAttribute('disabled', '');
        submitBtn.textContent = 'Sending...';
        const channel = channelSelect.value as 'email' | 'phone';
        try {
            const unreachable = await findHomeNode(cid);
            if (unreachable) {
                submitBtn.removeAttribute('disabled');
                submitBtn.textContent = 'Send confirmation code';
                showError(unreachable);
                return;
            }
            const begin = await accountApi.beginReset({
                cid,
                sealedToken: aes.toBase64(sealedToken),
                channel,
            });
            showResetConfirmStage(card, errorArea, callbacks, {
                cid, channel,
                resetJobId: begin.resetJobId,
                resetSecret: begin.resetSecret,
                verifyToken: begin.verifyToken ?? null,
            });
        } catch (err) {
            submitBtn.removeAttribute('disabled');
            submitBtn.textContent = 'Send confirmation code';
            if (isOriginOnlyError(err)) {
                showError(HOME_NODE_MOVED);
                return;
            }
            showError(err instanceof Error ? err.message : 'Could not start the ceremony');
        }
    });
    form.appendChild(submitBtn);

    appendBackToLogin(form, card, callbacks);
    card.appendChild(form);
}

interface ResetStageContext {
    cid: string;
    channel: 'email' | 'phone';
    resetJobId: string;
    resetSecret: string;
    verifyToken: string | null;
}

/** Code confirmation: phone enters the SMS code here; email clicks the link on
 *  any device. Either way this client polls until the job confirms. */
function showResetConfirmStage(
    card: HTMLElement, errorArea: HTMLElement, callbacks: LoginViewCallbacks, rctx: ResetStageContext,
): void {
    const title = card.querySelector('.cid-auth-title');
    card.innerHTML = '';
    if (title) card.appendChild(title);
    card.appendChild(errorArea);
    errorArea.style.display = 'none';

    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Confirm with the code',
    }));
    card.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: rctx.channel === 'phone'
            ? 'If this sheet is valid, a confirmation code was sent to your phone. Enter it below.'
            : 'If this sheet is valid, a confirmation link was sent to your email. Open it on any '
            + 'device — this page will continue automatically.',
    }));
    // The decoy above is deliberately non-committal (the server answers an
    // unknown sheet the same way), so name where to look for the message.
    card.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: 'The confirmation goes to the email or phone number bound to this sheet.',
    }));

    const form = createElement('div', { className: 'cid-auth-form' });

    if (rctx.channel === 'phone' && rctx.verifyToken) {
        const codeInput = createInput('Confirmation code', 'text', 'cid-reset-code');
        codeInput.input.inputMode = 'numeric';
        codeInput.input.autocomplete = 'one-time-code';
        form.appendChild(codeInput.wrapper);

        const confirmBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cid-auth-submit',
            textContent: 'Confirm code',
        });
        confirmBtn.addEventListener('click', async () => {
            const code = codeInput.input.value.trim();
            if (!code) { errorArea.textContent = 'Enter the code from the text message'; errorArea.style.display = 'block'; return; }
            errorArea.style.display = 'none';
            confirmBtn.setAttribute('disabled', '');
            confirmBtn.textContent = 'Checking...';
            try {
                const result = await claimsApi.verifyPhone(rctx.verifyToken!, code, rctx.cid);
                if (!result.success) {
                    confirmBtn.removeAttribute('disabled');
                    confirmBtn.textContent = 'Confirm code';
                    errorArea.textContent = 'That code was not accepted — check the text message and try again.';
                    errorArea.style.display = 'block';
                    return;
                }
                confirmBtn.textContent = 'Confirmed — waiting...';
                // The validator's callback flips the job to confirmed moments
                // after the check — the poll below picks it up.
            } catch {
                confirmBtn.removeAttribute('disabled');
                confirmBtn.textContent = 'Confirm code';
                errorArea.textContent = 'That code was not accepted — check the text message and try again.';
                errorArea.style.display = 'block';
            }
        });
        form.appendChild(confirmBtn);
    } else {
        const waiting = createProgressBar({ indeterminate: true, label: 'Waiting for the link to be opened...' });
        form.appendChild(waiting.element);
    }

    appendBackToLogin(form, card, callbacks);
    card.appendChild(form);

    // Poll until confirmed. The logged-out client has no socket; 2.5 s keeps
    // the wait tight without hammering the endpoint. Stops when this stage
    // leaves the DOM (back-to-login or stage change) or the job dies.
    const poll = setInterval(async () => {
        if (!form.isConnected) { clearInterval(poll); return; }
        try {
            const status = await accountApi.resetStatus({
                resetJobId: rctx.resetJobId, resetSecret: rctx.resetSecret,
            }, rctx.cid);
            if (status === 'confirmed') {
                clearInterval(poll);
                showResetChoiceStage(card, errorArea, callbacks, rctx);
            } else if (status === 'expired' || status === 'consumed') {
                clearInterval(poll);
                errorArea.textContent = 'This has expired — start again from the login page.';
                errorArea.style.display = 'block';
            }
        } catch { /* transient — keep polling */ }
    }, 2500);
}

/** The job is confirmed. §12 makes the password change OPTIONAL — a lock raised
 *  "just in case" is cured by the unlock alone, and forcing a new password on a
 *  member who still knows theirs is friction with no security gain. Setting one
 *  is nevertheless the DEFAULT: an unlocked account whose password was never
 *  changed reopens to whoever knows that password.
 *
 *  A generated password matches the system's credential model (registration
 *  generates one too) — the member saves it in their password manager. */
function showResetChoiceStage(
    card: HTMLElement, errorArea: HTMLElement, callbacks: LoginViewCallbacks, rctx: ResetStageContext,
): void {
    const title = card.querySelector('.cid-auth-title');
    card.innerHTML = '';
    if (title) card.appendChild(title);
    card.appendChild(errorArea);
    errorArea.style.display = 'none';

    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Set a new password?',
    }));
    card.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: 'If this account was locked, finishing here unlocks it. Everything currently '
            + 'signed in is signed out either way. Your keys and data are untouched.',
    }));

    const form = createElement('div', { className: 'cid-auth-form' });

    const newPassword = generatePassword();
    const choiceName = 'cid-reset-password-choice';

    const setChoice = createChoice(choiceName, 'Set a new password', true);
    form.appendChild(setChoice.wrapper);

    // The generated password sits under its option so it reads as part of it.
    const pwPanel = createElement('div', { className: 'cid-auth-field' });
    pwPanel.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: 'Save this in your password manager now — it becomes your login password the '
            + 'moment you finish.',
    }));
    const pwRow = createElement('div', { className: 'cid-auth-field' });
    const pwInput = document.createElement('input');
    pwInput.type = 'text';
    pwInput.className = 'cid-auth-input';
    pwInput.readOnly = true;
    pwInput.value = newPassword;
    pwRow.appendChild(pwInput);
    pwRow.appendChild(createCopyButton({ getText: () => newPassword }));
    pwPanel.appendChild(pwRow);
    form.appendChild(pwPanel);

    const keepChoice = createChoice(
        choiceName, 'Keep my current password (the account was locked just in case)', false);
    form.appendChild(keepChoice.wrapper);

    const syncPanel = () => { pwPanel.style.display = setChoice.input.checked ? '' : 'none'; };
    setChoice.input.addEventListener('change', syncPanel);
    keepChoice.input.addEventListener('change', syncPanel);
    syncPanel();

    // Round 8 (§15.6): the second, INDEPENDENT choice — a checkbox, not part of
    // the password radio group. Default OFF: most unlocks are about a lock, not
    // a lost phone, and reissuing voids a sheet that is probably fine.
    const totpWrapper = createElement('div', { className: 'cid-auth-field' });
    const totpLabel = createElement('label', {
        style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' },
    });
    const totpCheckbox = document.createElement('input');
    totpCheckbox.type = 'checkbox';
    totpCheckbox.id = 'cid-reset-reissue-totp';
    totpCheckbox.checked = false;
    totpLabel.appendChild(totpCheckbox);
    totpLabel.appendChild(createElement('span', { textContent: 'Issue a new Authenticator Key' }));
    totpWrapper.appendChild(totpLabel);
    totpWrapper.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: 'Choose this if you lost the phone or the sheet. Your login code stays off until '
            + 'you scan the new key and confirm it.',
    }));
    form.appendChild(totpWrapper);

    const finishBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Finish',
    });
    finishBtn.addEventListener('click', async () => {
        errorArea.style.display = 'none';
        finishBtn.setAttribute('disabled', '');
        finishBtn.textContent = 'Finishing...';
        try {
            const newAuthHash = setChoice.input.checked
                ? await computeAuthHash(newPassword, rctx.cid)
                : undefined;
            const outcome = await accountApi.completeReset({
                resetJobId: rctx.resetJobId,
                resetSecret: rctx.resetSecret,
                newAuthHash,
                reissueTotp: totpCheckbox.checked,
            }, rctx.cid);
            showResetDoneStage(card, errorArea, callbacks, outcome, rctx.cid);
        } catch (err) {
            finishBtn.removeAttribute('disabled');
            finishBtn.textContent = 'Finish';
            errorArea.textContent = err instanceof Error
                ? err.message
                : 'Could not finish — the confirmation may have expired.';
            errorArea.style.display = 'block';
        }
    });
    form.appendChild(finishBtn);

    appendBackToLogin(form, card, callbacks);
    card.appendChild(form);
}

/** What happened, what it cost (every session), and the one follow-up worth
 *  making: reissue the sheet. Reissuing needs a session with validated claims
 *  (ResetCardController.Issue), so it cannot happen from this anonymous flow —
 *  the offer explains where to do it and sends the member to log in. */
function showResetDoneStage(
    card: HTMLElement, errorArea: HTMLElement, callbacks: LoginViewCallbacks,
    outcome: ResetOutcome & { totpSecret?: string; totpUri?: string },
    cid: string,
): void {
    const title = card.querySelector('.cid-auth-title');
    card.innerHTML = '';
    if (title) card.appendChild(title);
    card.appendChild(errorArea);
    errorArea.style.display = 'none';

    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: resetOutcomeHeadline(outcome),
    }));
    card.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: resetOutcomeDetail(outcome),
    }));

    const form = createElement('div', { className: 'cid-auth-form' });

    // The reissued Authenticator Key, shown ONCE (§15.6): the node hands it
    // back with the completion and never again without a session and a step-up.
    if (outcome.totpReissued && outcome.totpSecret && outcome.totpUri) {
        appendAuthenticatorKeyPanel(form, {
            cid,
            secret: base32Decode(outcome.totpSecret),
            uri: outcome.totpUri,
            intro: 'Scan this with your authenticator app, then print the replacement sheet for your '
                + 'kit. It is shown once — leaving this screen without printing means issuing another '
                + 'key from Settings.',
        });
    }

    form.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Invalidate this sheet and print a new one',
    }));
    form.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: 'Your sheet still works — it stays valid after every use. Recommended if the '
            + 'account was locked because you suspected something was wrong, or if the sheet itself '
            + 'may have been copied or photographed. Log in, then open Settings and issue a new '
            + 'Account Unlock Sheet: issuing one permanently invalidates the old sheet. Print the new '
            + 'sheet and destroy the old printout.',
    }));

    const loginBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Log in',
    });
    loginBtn.addEventListener('click', () => renderLoginView(card.parentElement!, callbacks));
    form.appendChild(loginBtn);

    card.appendChild(form);
}

// ============================================================================
// The account's home server (docs/cid-governance.md D18, M7a)
// ============================================================================

/** Shown when a node still sends the account elsewhere after the client
 *  followed its answer once — nothing the person can fix from here. */
const HOME_NODE_MOVED = "This account's home server could not be reached from here. Please try again later.";

/** Shown when the account's home server has no address at all right now. */
function homeNodeUnreachable(code: string): string {
    return `This account's home server (${code}) cannot be reached right now. Please try again later.`;
}

/**
 * Find where `cid` lives before anything is sent for it: every session is
 * created at the account's origin, so the password goes THERE and nowhere else.
 * The entry node answers; a found origin is remembered for the identity, so the
 * login, the device unlock and everything after talk to it. Returns the sentence
 * to show when the home server cannot be used, else null. An unknown CID, or an
 * entry node that cannot answer the question, proceeds: the login then fails
 * the ordinary way, identically for every unknown account.
 */
async function findHomeNode(cid: string): Promise<string | null> {
    const answer = await lookupHomeNode(cid);
    return answer?.kind === 'unreachable' ? homeNodeUnreachable(answer.code) : null;
}

/** {@link findHomeNode}'s answer itself; null when the entry node could not be asked. */
async function lookupHomeNode(cid: string): Promise<OriginLookup | null> {
    try {
        return await resolveOrigin(cid);
    } catch {
        return null;
    }
}

/** Install the session a login answered with. One place, so the password leg
 *  and the authenticator leg cannot drift. */
function startSessionFromLogin(resp: LoginResponse): void {
    session.start(resp.sessionToken, {
        cid: resp.cid,
        accountType: resp.accountType,
        status: resp.status,
        isOperator: resp.isOperator,
        sessionId: resp.sessionId,
    });
    identity.setActive({ cid: resp.cid, accountType: resp.accountType });
    deviceStorage.setActiveCid(resp.cid);
}

/**
 * The Authenticator Key login gate's second leg (registration-restructure
 * §15.5/§15.6): the password verified, the node held the session back and
 * handed us a five-minute pre-auth token. One field; it submits itself the
 * moment six digits are in, because nobody wants to press a button after typing
 * a code they can already see expiring.
 *
 * A wrong code leaves the pre-auth token alive (the node only retires it on
 * success), so the member retries here rather than at the password screen. The
 * per-ACCOUNT limit is the thing that stops guessing, and a 429 is shown as
 * what it is.
 *
 * The device-unlock path is unaffected: a device that already holds a session
 * token never reaches a login, and the gate is on SESSION CREATION.
 */
function showTotpCodeStage(
    container: HTMLElement,
    card: HTMLElement,
    errorArea: HTMLElement,
    callbacks: LoginViewCallbacks,
    ctx: { cid: string; preAuthToken: string },
): void {
    const title = card.querySelector('.cid-auth-title');
    card.innerHTML = '';
    if (title) card.appendChild(title);
    card.appendChild(errorArea);
    errorArea.style.display = 'none';

    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Enter your login code',
    }));
    card.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: `Account ${ctx.cid}. Open your authenticator app and type the six digits it is `
            + 'showing for Catalog.ID.',
    }));

    const form = createElement('div', { className: 'cid-auth-form' });
    const codeField = createElement('div', { className: 'cid-auth-field' });
    const codeInput = document.createElement('input');
    codeInput.type = 'text';
    codeInput.id = 'cid-login-totp';
    codeInput.className = 'cid-auth-input';
    codeInput.placeholder = '000000';
    codeInput.inputMode = 'numeric';
    codeInput.autocomplete = 'one-time-code';
    codeInput.maxLength = 6;
    codeField.appendChild(codeInput);
    form.appendChild(codeField);

    const submitBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Continue',
    });

    let submitting = false;
    const submit = async (): Promise<void> => {
        if (submitting) return;
        const code = normalizeAuthenticatorCode(codeInput.value);
        if (!isCompleteAuthenticatorCode(code)) return;
        submitting = true;
        errorArea.style.display = 'none';
        submitBtn.setAttribute('disabled', '');
        submitBtn.textContent = 'Checking...';
        try {
            const resp = await submitTotpLogin({ cid: ctx.cid, preAuthToken: ctx.preAuthToken, code });
            startSessionFromLogin(resp);
            showDeviceUnlock(card, errorArea, withSessionLostDefault(container, callbacks));
        } catch (err) {
            submitting = false;
            submitBtn.removeAttribute('disabled');
            submitBtn.textContent = 'Continue';
            errorArea.textContent = describeAuthenticatorError(err);
            errorArea.style.display = 'block';
            codeInput.value = '';
            codeInput.focus();
        }
    };

    codeInput.addEventListener('input', () => {
        const normalized = normalizeAuthenticatorCode(codeInput.value);
        if (codeInput.value !== normalized) codeInput.value = normalized;
        // Auto-submit at six digits (§15.6).
        if (isCompleteAuthenticatorCode(normalized)) void submit();
    });
    codeInput.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') void submit();
    });
    submitBtn.addEventListener('click', () => { void submit(); });
    form.appendChild(submitBtn);

    // The loss path is the Unlock Sheet, not a strip of backup codes (§15.1).
    const linksRow = createElement('div', { className: 'cid-auth-link-row' });
    const lostLink = createElement('a', {
        className: 'cid-auth-link',
        textContent: 'Lost your authenticator? Use your Account Unlock Sheet',
    });
    lostLink.setAttribute('href', '#');
    lostLink.addEventListener('click', (e) => {
        e.preventDefault();
        showResetPassword(card, errorArea, callbacks);
    });
    linksRow.appendChild(lostLink);
    form.appendChild(linksRow);

    appendBackToLogin(form, card, callbacks);
    card.appendChild(form);
    codeInput.focus();
}

/** A radio option with its label — the two-way "set a new password?" choice. */
function createChoice(
    name: string, label: string, checked: boolean,
): { wrapper: HTMLElement; input: HTMLInputElement } {
    const wrapper = createElement('div', { className: 'cid-auth-field' });
    const labelEl = createElement('label', {
        style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' },
    });
    const input = document.createElement('input');
    input.type = 'radio';
    input.name = name;
    input.checked = checked;
    labelEl.appendChild(input);
    labelEl.appendChild(createElement('span', { textContent: label }));
    wrapper.appendChild(labelEl);
    return { wrapper, input };
}

function appendBackToLogin(form: HTMLElement, card: HTMLElement, callbacks: LoginViewCallbacks): void {
    const backRow = createElement('div', { className: 'cid-auth-link-row' });
    const backLink = createElement('a', { className: 'cid-auth-link', textContent: 'Back to login' });
    backLink.setAttribute('href', '#');
    backLink.addEventListener('click', (e) => {
        e.preventDefault();
        renderLoginView(card.parentElement!, callbacks);
    });
    backRow.appendChild(backLink);
    form.appendChild(backRow);
}

// ============================================================================
// Helpers
// ============================================================================

async function loadKeysFromDevice(cid: string, deviceEncryptionKey: Uint8Array): Promise<void> {
    // Hand the device-slot store this identity's key while we have it
    // (registration-wallets §4.12, D12). Without it every read of the cached
    // `catalog:default` token answers null and every operator-payee key-grant
    // request costs the member a supersecret prompt — for a value that is
    // sitting encrypted in this browser's own storage. Both unlock paths (token
    // and PIN) come through here, which is why it is here and not at each.
    rememberSlotKey(cid, deviceEncryptionKey);
    const decrypted = await deviceStorage.decryptAllStoredEntries(cid, deviceEncryptionKey);
    await assertDeviceCopyCurrent(cid);
    const entries = decrypted.map(e => ({
        keyId: e.keyId,
        algorithm: e.algorithm,
        privateKey: aes.toBase64(e.privateKey),
        keyIndex: e.keyIndex,
        status: e.status as 'active' | 'revoked',
    }));
    keys.load({ entries });
}

/**
 * Refuse to load a device copy that lags the server bag.
 *
 * The copy is written at enrollment and never by the flows that append keys
 * later (ceremony authority key, derive root, key-grant, bitcash wallet), nor
 * by another device — see `deviceStorage.findEntriesMissingFromDevice`. Both
 * unlock variants come through `loadKeysFromDevice`, and the server is
 * reachable (the unlock call just succeeded), so one keybag read is the whole
 * cost. A lagging copy is the same situation as an undecryptable one: throw
 * the stale-keys error so the unlock screen routes to supersecret unlock,
 * whose device-setup step rewrites the full copy. If the read fails for any
 * reason other than a dead session, the copy is all there is — load it rather
 * than lock the user out.
 */
async function assertDeviceCopyCurrent(cid: string): Promise<void> {
    let serverEntries: { keyId: string; keyIndex: number }[];
    try {
        serverEntries = (await accountApi.getKeyBag()).entries;
    } catch (err) {
        if (isSessionRejected(err)) throw err;
        return;
    }
    // §7.3: the reconcile reads this device's key manifest. Without it every
    // device that declined a key would be "stale" on every login and selective
    // import would be a lockout rather than a feature (plan §12).
    const missing = deviceStorage.findEntriesMissingFromDevice(
        cid, serverEntries, readDeviceKeyManifest(cid));
    if (missing.length === 0) return;
    const list = missing.map(m => `${m.keyId}@${m.keyIndex}`).join(', ');
    console.warn(`Device key copy for ${cid} lags the server bag — missing ${list}; falling back to supersecret unlock.`);
    throw new deviceStorage.StaleDeviceKeysError(
        `Device copy is missing ${missing.length} key(s) held on the server: ${list}.`);
}

function createInput(
    placeholder: string, type: string, id: string,
): { wrapper: HTMLElement; input: HTMLInputElement } {
    const wrapper = createElement('div', { className: 'cid-auth-field' });
    const input = document.createElement('input');
    input.type = type;
    input.id = id;
    input.className = 'cid-auth-input';
    input.placeholder = placeholder;
    input.autocomplete = type === 'password' ? 'current-password' : 'username';
    wrapper.appendChild(input);
    return { wrapper, input };
}
